import type { TableSchema } from './tableSchema.types';

const DEFAULT_TABLE_DIMS = { width: 240, height: 180 };

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const getHeaderConfig = (schema: TableSchema) => {
  const row0 = schema.rows?.find((r) => r.index === 0);
  const showHeader = !!row0?.isHeader;
  const headerHeight = typeof row0?.height === 'number' && Number.isFinite(row0.height) ? row0.height : undefined;
  const headerFillColor = schema.theme?.headerBackground;
  const col0 = schema.cols?.find((c) => c.index === 0);
  const showHeaderColumn =
    !!col0?.isHeader &&
    (typeof col0.label !== 'string' || col0.label.trim().length === 0);
  const headerRowIndices = (schema.rows ?? []).filter((r) => r?.isHeader === true).map((r) => r.index);
  const headerColIndices = (schema.cols ?? []).filter((c) => c?.isHeader === true).map((c) => c.index);
  return { showHeader, headerHeight, headerFillColor, showHeaderColumn, headerRowIndices, headerColIndices };
};

export const resolveTableShapeConfigFromSchema = (schema: TableSchema): {
  width: number;
  height: number;
  attrs: Record<string, unknown>;
} => {
  const rowCount = clampCount(schema.grid.rows, 3);
  const columnCount = clampCount(schema.grid.cols, 3);

  const { showHeader, headerHeight, headerFillColor, showHeaderColumn, headerRowIndices, headerColIndices } =
    getHeaderConfig(schema);

  const defaultRowHeight = schema.dimensions?.defaultRowHeight;
  const defaultColWidth = schema.dimensions?.defaultColWidth;
  const minCellHeight =
    typeof schema.dimensions?.minCellHeight === 'number' && Number.isFinite(schema.dimensions.minCellHeight)
      ? schema.dimensions.minCellHeight
      : 20;
  const minCellWidth =
    typeof schema.dimensions?.minCellWidth === 'number' && Number.isFinite(schema.dimensions.minCellWidth)
      ? schema.dimensions.minCellWidth
      : 20;

  const width =
    typeof defaultColWidth === 'number' && Number.isFinite(defaultColWidth)
      ? defaultColWidth * columnCount
      : DEFAULT_TABLE_DIMS.width;

  const baseHeaderHeight =
    typeof headerHeight === 'number' && Number.isFinite(headerHeight)
      ? Math.max(minCellHeight, headerHeight)
      : showHeader
        ? Math.max(minCellHeight, 32)
        : 0;

  const height =
    typeof defaultRowHeight === 'number' && Number.isFinite(defaultRowHeight)
      ? defaultRowHeight * rowCount + (showHeader ? baseHeaderHeight : 0)
      : DEFAULT_TABLE_DIMS.height;

  const bodyHeight = Math.max(1, height - (showHeader ? baseHeaderHeight : 0));

  const rowHeights =
    typeof defaultRowHeight === 'number' && Number.isFinite(defaultRowHeight)
      ? Array(rowCount).fill(Math.max(minCellHeight, defaultRowHeight))
      : Array(rowCount).fill(Math.max(minCellHeight, bodyHeight / Math.max(1, rowCount)));

  const headerCol0 = showHeaderColumn ? schema.cols?.find((c) => c.index === 0 && c.isHeader) : undefined;
  const headerColumnWidth =
    showHeaderColumn && typeof headerCol0?.width === 'number' && Number.isFinite(headerCol0.width)
      ? Math.max(minCellWidth, headerCol0.width)
      : showHeaderColumn
        ? Math.max(minCellWidth, 30)
        : undefined;

  const bodyColumnWidths =
    typeof defaultColWidth === 'number' && Number.isFinite(defaultColWidth)
      ? Array(columnCount).fill(Math.max(minCellWidth, defaultColWidth))
      : Array(columnCount).fill(Math.max(minCellWidth, width / Math.max(1, columnCount)));

  const columnWidths =
    showHeaderColumn && headerColumnWidth !== undefined
      ? [headerColumnWidth, ...bodyColumnWidths]
      : bodyColumnWidths;

  const finalColumnCount = showHeaderColumn ? columnCount + 1 : columnCount;

  const cellTextFromSchema = (() => {
    const map: Record<string, string> = {};

    for (const c of schema.cols ?? []) {
      if (typeof c.label === 'string' && c.label.trim().length > 0) {
        map[`0,${c.index}`] = c.label;
      }
    }

    for (const cell of schema.cells ?? []) {
      if (typeof cell.text === 'string' && cell.text.trim().length > 0) {
        map[`${cell.row},${cell.col}`] = cell.text;
      }
    }

    return map;
  })();

  const cellStylesFromSchema = (() => {
    const map: Record<string, unknown> = {};
    for (const cell of schema.cells ?? []) {
      if (!cell) continue;
      const style = cell.style ? { ...cell.style } : {};
      if (typeof cell.textRotation === 'number' && Number.isFinite(cell.textRotation)) {
        const rotation = cell.textRotation;
        if (rotation === 0 || rotation === 90 || rotation === -90 || rotation === 180) {
          (style as Record<string, unknown>).textRotation = rotation;
        }
      }
      if (Object.keys(style).length > 0) {
        map[`${cell.row},${cell.col}`] = style;
      }
    }
    return map;
  })();

  const cellLayoutsFromSchema = (() => {
    const map: Record<string, unknown> = {};
    for (const cell of schema.cells ?? []) {
      const layout = cell.layout;
      if (layout?.algorithmId === 'grid') {
        map[`${cell.row},${cell.col}`] = { algorithmId: 'grid', params: layout.params };
      }
    }
    return map;
  })();

  const finalWidth =
    showHeaderColumn && headerColumnWidth !== undefined
      ? width + headerColumnWidth
      : width;

  const attrs: Record<string, unknown> = {
    rowCount,
    columnCount: finalColumnCount,
    rowHeights,
    columnWidths,
    showHeader,
    showHeaderColumn,
    headerRowIndices,
    headerColIndices,
    ...(showHeader ? { headerHeight: baseHeaderHeight } : {}),
    ...(typeof headerFillColor === 'string' ? { headerFillColor } : {}),
    ...(typeof schema.theme?.borderColor === 'string' ? { gridColor: schema.theme.borderColor } : {}),
    ...(typeof schema.theme?.borderWidth === 'number' && Number.isFinite(schema.theme.borderWidth)
      ? { strokeWidth: schema.theme.borderWidth }
      : {}),
    ...(Object.keys(cellTextFromSchema).length > 0 ? { cellText: cellTextFromSchema } : {}),
    ...(Object.keys(cellStylesFromSchema).length > 0 ? { cellStyles: cellStylesFromSchema } : {}),
    ...(Object.keys(cellLayoutsFromSchema).length > 0 ? { cellLayouts: cellLayoutsFromSchema } : {}),
    mergedRegions: schema.mergedRegions ?? [],
    schemaId: schema.id,
  };

  return {
    width: finalWidth,
    height,
    attrs,
  };
};
